import React, { CSSProperties } from "react";
import { useSelector } from "react-redux";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faMinus, faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Avatar, Tooltip } from "@performyard/py-components";

import type { OrgEmployee } from "../types";

import { getUserID } from "../../../selectors/app";

import { employeeDetailsLinkWithFrom } from "../../../utils/employeeDetailsBreadcrumbs";
import { cx } from "../utils";

import css from "../OrgChart.module.scss";
import { getDisplayFields } from "../data";

export function EmployeeCard({
	employee,
	style,
	childCount,
	isExpanded,
	isHighlighted,
	isSelected,
	onToggle,
	onClick,
}: {
	employee: OrgEmployee;
	style: CSSProperties;
	childCount: number;
	isExpanded: boolean;
	isHighlighted: boolean;
	isSelected: boolean;
	onToggle: () => void;
	onClick: () => void;
}) {
	const hasChildren = childCount > 0;
	const currentUserId = useSelector(getUserID);
	const displayFields = getDisplayFields(employee);
	const firstDisplayField = displayFields[0];
	const primaryField = firstDisplayField
		? firstDisplayField.value || `No ${firstDisplayField.name.toLowerCase()}`
		: "No title";
	const secondaryField = displayFields
		.slice(1)
		.map((field) => field.value)
		.find(Boolean);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			onClick();
		}
	};

	return (
		<div
			data-interactive
			className={css.employeeNode}
			style={style}
			onClick={onClick}
			onKeyDown={handleKeyDown}
			role="treeitem"
			tabIndex={0}
			aria-selected={isSelected}
			aria-label={`Select ${employee.name}`}
		>
			<div className={css.employeeNodeInner}>
				{isHighlighted && <span aria-hidden className={css.highlightPing} />}
				<div
					className={cx(
						css.employeeCard,
						isHighlighted && css.employeeHighlighted,
						isSelected && css.employeeSelected
					)}
				>
					<div className={css.employeeHeader}>
						<Avatar id={employee.id} name={employee.name} size={40} />
						<a
							data-interactive
							className={css.employeeName}
							href={employeeDetailsLinkWithFrom(employee.id, currentUserId)}
							onClick={(event) => event.stopPropagation()}
						>
							{employee.name}
						</a>
					</div>
					<div className={css.employeeDetails}>
						<div className={css.employeeRole}>{primaryField}</div>
						{secondaryField && <div className={css.employeeEmail}>{secondaryField}</div>}
					</div>
				</div>

				{hasChildren && (
					<div className="absolute top-2 right-2 flex h-[22px] min-w-[20px] items-center justify-center rounded-md bg-neutral-6 px-0.5 text-caption leading-none font-semibold text-white tabular-nums shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]">
						{childCount}
					</div>
				)}

				{hasChildren && (
					<Tooltip content={isExpanded ? "Collapse direct reports" : "Expand direct reports"} asChild>
						<button
							type="button"
							data-interactive
							className={css.toggleButton}
							onClick={(event) => {
								event.stopPropagation();
								onToggle();
							}}
							aria-label={isExpanded ? "Collapse direct reports" : "Expand direct reports"}
						>
							<FontAwesomeIcon icon={isExpanded ? faMinus : faPlus} />
						</button>
					</Tooltip>
				)}
			</div>
		</div>
	);
}
